import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, FlaskConical, Loader2 } from 'lucide-react';
import { PrescriptionFooterProps } from './types';

const PrescriptionFooter: React.FC<PrescriptionFooterProps> = ({
  showReview,
  showFollowupLab,
  activeTab,
  selectedCount,
  reviewCount,
  mode,
  isSubmitting,
  labPanelSelectionEnabled,
  orderFollowupLab,
  selectedJunctionTest,
  onCancel,
  onBackToEdit,
  onBackToReview,
  onReview,
  onProceedFromReview,
  onSubmit,
  hasPriceWarning = false,
  priceWarningAcknowledged = false,
  discountError = false,
  isReviewing = false,
  treatmentTypeMissing = false,
  practitionerMissing = false,
}) => {
  // Followup Lab Step
  if (showFollowupLab) {
    return (
      <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
        <Button
          variant='outline'
          type='button'
          className='font-medium text-slate-700 hover:text-slate-900'
          onClick={onBackToReview}
        >
          <ArrowLeft className='mr-2 h-4 w-4' />
          Back to Review
        </Button>
        <Button
          type='button'
          onClick={onSubmit}
          className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
          disabled={isSubmitting || (orderFollowupLab && !selectedJunctionTest) || (hasPriceWarning && !priceWarningAcknowledged) || discountError || treatmentTypeMissing || practitionerMissing}
        >
          {isSubmitting ? (
            <div className='flex items-center space-x-2'>
              <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
              <span>{mode === 'create' ? 'Creating...' : 'Updating...'}</span>
            </div>
          ) : (
            <span>{mode === 'create' ? 'Create Order' : 'Update Order'}</span>
          )}
        </Button>
      </div>
    );
  }

  // Review Step
  if (showReview) {
    return (
      <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
        <Button
          variant='outline'
          type='button'
          className='font-medium text-slate-700 hover:text-slate-900'
          onClick={onBackToEdit}
        >
          <ArrowLeft className='mr-2 h-4 w-4' />
          Back to Edit
        </Button>
        {labPanelSelectionEnabled ? (
          <Button
            type='button'
            onClick={onProceedFromReview}
            className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
            disabled={isSubmitting || reviewCount === 0 || (hasPriceWarning && !priceWarningAcknowledged) || discountError || treatmentTypeMissing || practitionerMissing}
          >
            <FlaskConical className='mr-2 h-4 w-4' />
            Follow-up Lab
            <ArrowRight className='ml-2 h-4 w-4' />
          </Button>
        ) : (
          <Button
            type='button'
            onClick={onSubmit}
            className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
            disabled={isSubmitting || reviewCount === 0 || (hasPriceWarning && !priceWarningAcknowledged) || discountError || treatmentTypeMissing || practitionerMissing}
          >
            {isSubmitting ? (
              <div className='flex items-center space-x-2'>
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent' />
                <span>{mode === 'create' ? 'Creating...' : 'Updating...'}</span>
              </div>
            ) : (
              <span>{mode === 'create' ? 'Create Order' : 'Update Order'}</span>
            )}
          </Button>
        )}
      </div>
    );
  }

  // Normal Form Entry
  return (
    <div className='flex items-center justify-between border-t border-slate-200 px-6 py-4'>
      <Button
        variant='ghost'
        type='button'
        className='font-medium text-slate-700 hover:bg-transparent hover:text-slate-900'
        onClick={onCancel}
      >
        Cancel
      </Button>
      <Button
        type='button'
        onClick={onReview}
        className='h-11 rounded-lg bg-brand-teal px-8 hover:bg-brand-teal/90'
        disabled={isReviewing || (activeTab === 'storefront' && selectedCount === 0)}
      >
        {isReviewing ? (
          <div className='flex items-center space-x-2'>
            <Loader2 className='h-4 w-4 animate-spin' />
            <span>Checking...</span>
          </div>
        ) : (
          <span>
            {activeTab === 'storefront'
              ? `Review Order${selectedCount > 0 ? ` (${selectedCount})` : ''}`
              : 'Review Order'}
          </span>
        )}
      </Button>
    </div>
  );
};

export default PrescriptionFooter;
